import { memo } from 'react'
import { usePersonalSettingDisplayForm } from './usePersonalSettingDisplayForm'
import { CrewSelectBoxField } from 'components/forms/crewSelectBoxField'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { CrewRequireDot } from 'components/elements/crewRequireDot'
import { useTranslation } from '@crew/modules/i18n'
import { useUserSetting, useLoadUserSettings } from '@crew/states'
import { ROWS_PER_PAGE_OPTIONS } from '@crew/configs/constants'
import { useToast } from 'hooks/useToast'
import { useShowApiErrorsWithForm } from 'hooks/useShowApiErrors'
import {
  EventTimePeriod,
  FavoriteDisplayOrderTypeValues,
  SettingKeyType,
  Sort,
} from '@crew/enums/app'
import { useCallback, useEffect, useMemo } from 'react'
import {
  AppTheme,
  NotifyEventType,
  UserChatSettingDisplayFormat,
  UserChatSettingDisplayRange,
} from 'enums/app'
import {
  DEFAULT_LIST_DISPLAY_NUMBER,
  FormValues,
  formInitialValues,
} from './usePersonalSettingDisplayForm'
import { CrewCheckBoxField } from 'components/forms/crewCheckBoxField'
import { FavoriteDisplayOrderType } from '@crew/enums/domain'
import { Favorite } from '@crew/models/dist/domain'
import {
  ObjectEventMessage,
  notifyFavoriteEvent,
} from 'features/app/states/appSlice'
import { useDispatch } from 'react-redux'
export const PersonalSettingDisplayForm = memo(() => {
  const {
    control,
    reset,
    handleSubmit,
    setError,
    validateRules,
    updateUserSettings,
    isLoadingInsertOrReplaceSetting,
  } = usePersonalSettingDisplayForm()

  const { t } = useTranslation()
  const { success } = useToast()
  const [showApiErrors] = useShowApiErrorsWithForm(setError)
  const dispatch = useDispatch()

  const [loadUserSettings] = useLoadUserSettings()

  // ログイン中のユーザーの表示設定を取得する
  const defaultAppearanceTheme = useUserSetting(
    SettingKeyType.AppearanceTheme,
    AppTheme.Light
  )
  const defaultChatDisplayFormat = useUserSetting(
    SettingKeyType.ChatDisplayFormat,
    UserChatSettingDisplayFormat.Timeline.value
  )
  const defaultChatDisplayRange = useUserSetting(
    SettingKeyType.ChatDisplayRange,
    UserChatSettingDisplayRange.DisplayAll.value
  )
  const defaultListDisplayNumber = useUserSetting(
    SettingKeyType.ListDisplayNumber,
    DEFAULT_LIST_DISPLAY_NUMBER
  )
  const defaultTaskHistoryDisplayOrder = useUserSetting(
    SettingKeyType.TaskHistoryDisplayOrder,
    Sort.Ascending.value
  )
  const defaultShowNewArrival = useUserSetting(
    SettingKeyType.ShowNewArrival,
    'false'
  )
  const defaultShowMySchedule = useUserSetting(
    SettingKeyType.ShowMySchedule,
    'true'
  )
  const defaultShowMyTask = useUserSetting(SettingKeyType.ShowMyTask, 'true')
  const defaultShowStartUpGuide = useUserSetting(
    SettingKeyType.ShowStartUpGuide,
    'true'
  )
  const defaultDisplayTutorial = useUserSetting(
    SettingKeyType.DisplayTutorial,
    'true'
  )
  const defaultScheduleInitialDisplay = useUserSetting(
    SettingKeyType.ScheduleInitialDisplay,
    EventTimePeriod.GroupMonthly.value
  )
  const defaultFavoriteDisplayOrderType = useUserSetting(
    SettingKeyType.FavoriteDisplayOrderType,
    FavoriteDisplayOrderType.Activity
  )

  // 保存された設定を画面に表示する
  useEffect(() => {
    //theme
    formInitialValues.theme = String(defaultAppearanceTheme)
    //list display number
    formInitialValues.displayNumber = Number(defaultListDisplayNumber)
    //chat display format
    formInitialValues.displayFormat = String(defaultChatDisplayFormat)
    //chat display range
    formInitialValues.displayRange = String(defaultChatDisplayRange)
    //task history display order
    formInitialValues.historyDisplayOrder = String(
      defaultTaskHistoryDisplayOrder
    )
    // 表示: 新着の表示
    formInitialValues.showNewArrival =
      defaultShowNewArrival === 'true' ? true : false
    // 表示: マイスケジュールの表示
    formInitialValues.showMySchedule =
      defaultShowMySchedule === 'true' ? true : false
    // 表示: マイタスクの表示
    formInitialValues.showMyTask = defaultShowMyTask === 'true' ? true : false
    // 表示: スタートアップガイド
    formInitialValues.showStartUpGuide =
      defaultShowStartUpGuide === 'true' ? true : false
    // 表示: チュートリアル(操作ガイド)
    formInitialValues.displayTutorial =
      defaultDisplayTutorial === 'true' ? true : false
    // メニュー: スケジュール初期表示
    formInitialValues.scheduleInitialDisplay = String(
      defaultScheduleInitialDisplay
    )
    // ホーム: お気に入り表示順
    formInitialValues.favoriteDisplayOrderType = String(
      defaultFavoriteDisplayOrderType
    )

    reset({ ...formInitialValues })
  }, [
    defaultAppearanceTheme,
    defaultChatDisplayFormat,
    defaultChatDisplayRange,
    defaultShowMySchedule,
    defaultShowMyTask,
    defaultShowNewArrival,
    defaultListDisplayNumber,
    defaultTaskHistoryDisplayOrder,
    defaultShowStartUpGuide,
    defaultDisplayTutorial,
    reset,
    defaultScheduleInitialDisplay,
    defaultFavoriteDisplayOrderType,
  ])

  // Get the list of themes
  const appThemes = useMemo(() => {
    return Object.values(AppTheme).map((item) => {
      return {
        value: item,
        key: t(`label.${item}`),
      }
    })
  }, [t])

  // Get the list of display number
  const displayNumbers = useMemo(() => {
    return ROWS_PER_PAGE_OPTIONS.map((item) => {
      return {
        value: item,
        key: t('label.itemPerPage', { displayNumber: item }),
      }
    })
  }, [t])

  // Get the list of display format for chat message
  const displayFormats = useMemo(() => {
    return Object.values(UserChatSettingDisplayFormat).map((item) => {
      return {
        value: item.value,
        key: t(item.text),
      }
    })
  }, [t])

  // Get the list of display range for chat message
  const displayRanges = useMemo(() => {
    return Object.values(UserChatSettingDisplayRange).map((item) => {
      return {
        value: item.value,
        key: t(item.text),
      }
    })
  }, [t])

  // Get the list of history display order
  const historyDisplayOrders = useMemo(() => {
    return Object.values(Sort).map((item) => {
      return {
        value: item.value,
        key: t(item.text),
      }
    })
  }, [t])

  // Get the list of display format for chat message
  const scheduleInitialDisplayItems = useMemo(() => {
    return Object.values(EventTimePeriod).map((item) => {
      return {
        value: item.value,
        key: t(item.text),
      }
    })
  }, [t])

  // お気に入り表示順のリストに表示するアイテムを生成
  const favoriteDisplayOrderTypeItems = useMemo(() => {
    return Object.values(FavoriteDisplayOrderTypeValues).map((item) => ({
      value: item.value,
      key: t(item.text),
    }))
  }, [t])

  // Event handle when the Save button is clicked
  const handleSaveButtonClick = useCallback(() => {
    const onSubmit = async (data: FormValues) => {
      try {
        // お気に入り表示順がカスタムに変更された場合、更新後にアクティブチャットルームを再取得する
        const reloadActiveChatRoom =
          defaultFavoriteDisplayOrderType !== data.favoriteDisplayOrderType &&
          data.favoriteDisplayOrderType ===
            FavoriteDisplayOrderType.Custom.toString()

        // Execute update user setting process
        await updateUserSettings(data)

        // Display a toast indicating that the setting update was successful
        success(t('message.personalSetting.saveUserSettingSuccess'))

        // Reload the settings
        loadUserSettings()

        // お気に入り表示順がカスタムに変更された場合、表示順が振り直しされるのでアクティブチャットルームが再取得されるように通知する
        if (reloadActiveChatRoom) {
          const objectEventMessage: ObjectEventMessage<Favorite> = {
            eventType: NotifyEventType.Updated,
            id: '',
            object: undefined,
          }
          dispatch(notifyFavoriteEvent(objectEventMessage))
        }
      } catch (err) {
        // show error api
        showApiErrors(err)
      }
    }
    handleSubmit(onSubmit)()
  }, [
    defaultFavoriteDisplayOrderType,
    dispatch,
    handleSubmit,
    loadUserSettings,
    showApiErrors,
    success,
    t,
    updateUserSettings,
  ])

  return (
    <form className="flex flex-col gap-y-5">
      {/* 外観 */}
      <div
        id="personal-setting-display-form-appearance"
        className="flex flex-col gap-y-2.5 py-2.5 border-b crew-border-gray"
      >
        <p className="font-bold leading-5">{t('label.appearance')}</p>

        {/* 表示名 */}
        <div className="flex items-center justify-between">
          <span className="crew-text-gray-4">
            {t('label.theme')}
            <CrewRequireDot />
          </span>
          <CrewSelectBoxField
            control={control}
            id="theme"
            name="theme"
            className="w-80"
            items={appThemes}
            showClearButton={false}
            minSearchLength={0}
            rules={validateRules.theme}
            displayExpr="key"
            valueExpr="value"
          />
        </div>
      </div>

      {/* 表示 */}
      <div
        id="personal-setting-display-form-display"
        className="flex flex-col gap-y-2.5 py-2.5 border-b crew-border-gray"
      >
        <p className="font-bold leading-5">
          {t('label.personalSettingCategoryDisplay')}
        </p>

        {/* 新着 */}
        <div className="flex items-center justify-between h-8">
          <span className="crew-text-gray-4">{t('label.newArrival')}</span>
          <div className="w-80">
            <CrewCheckBoxField
              id="showNewArrival"
              control={control}
              name="showNewArrival"
              label={t('label.show')}
            />
          </div>
        </div>

        {/* マイスケジュール */}
        <div className="flex items-center justify-between h-8">
          <span className="crew-text-gray-4">{t('label.schedule')}</span>
          <div className="w-80">
            <CrewCheckBoxField
              id="showMySchedule"
              control={control}
              name="showMySchedule"
              label={t('label.show')}
            />
          </div>
        </div>

        {/* マイタスク */}
        <div className="flex items-center justify-between h-8">
          <span className="crew-text-gray-4">{t('label.myTask')}</span>
          <div className="w-80">
            <CrewCheckBoxField
              id="showMyTask"
              control={control}
              name="showMyTask"
              label={t('label.show')}
            />
          </div>
        </div>

        {/* 活用ガイド */}
        <div className="flex items-center justify-between h-8">
          <span className="crew-text-gray-4">
            {t('label.userGuide.userGuide')}
          </span>
          <div className="w-80">
            <CrewCheckBoxField
              id="showStartUpGuide"
              control={control}
              name="showStartUpGuide"
              label={t('label.show')}
            />
          </div>
        </div>

        {/* チュートリアル(操作ガイド) */}
        <div className="flex items-start justify-between h-10 mt-1">
          <span className="crew-text-gray-4">{t('label.tutorial')}</span>
          <div className="w-80 space-y-0.5">
            <CrewCheckBoxField
              id="displayTutorial"
              control={control}
              name="displayTutorial"
              label={t('label.show')}
            />
          </div>
        </div>
      </div>

      {/* ホーム */}
      <div className="flex flex-col gap-y-2.5 py-2.5 border-b crew-border-gray">
        <p className="font-bold leading-5">{t('label.home')}</p>

        {/* スタートページ */}
        {/* https://break-tmc.atlassian.net/browse/CREW-20413 で対応 */}

        {/* お気に入りの表示順 */}
        <div className="flex items-center justify-between">
          <span className="crew-text-gray-4">
            {t('label.favoriteDisplayOrderType')}
            <CrewRequireDot />
          </span>
          <CrewSelectBoxField
            control={control}
            id="favoriteDisplayOrderType"
            name="favoriteDisplayOrderType"
            className="w-80"
            items={favoriteDisplayOrderTypeItems}
            showClearButton={false}
            minSearchLength={0}
            rules={validateRules.favoriteDisplayOrderType}
            displayExpr="key"
            valueExpr="value"
          />
        </div>
      </div>

      {/* リスト */}
      <div
        id="personal-setting-display-form-list"
        className="flex flex-col gap-y-2.5 py-2.5 border-b crew-border-gray"
      >
        <p className="font-bold leading-5">{t('label.list')}</p>

        {/* 表示件数 */}
        <div className="flex items-center justify-between">
          <span className="crew-text-gray-4">
            {t('label.displayNumber')}
            <CrewRequireDot />
          </span>
          <CrewSelectBoxField
            control={control}
            id="displayNumber"
            name="displayNumber"
            className="w-80"
            items={displayNumbers}
            showClearButton={false}
            minSearchLength={0}
            rules={validateRules.displayNumber}
            displayExpr="key"
            valueExpr="value"
          />
        </div>
      </div>

      {/* チャット */}
      <div
        id="personal-setting-display-form-chat"
        className="flex flex-col gap-y-2.5 py-2.5 border-b crew-border-gray"
      >
        <p className="font-bold leading-5">{t('label.chat')}</p>

        {/* 表示形式 */}
        <div className="flex items-center justify-between">
          <span className="crew-text-gray-4">
            {t('label.displayFormat')}
            <CrewRequireDot />
          </span>
          <CrewSelectBoxField
            control={control}
            id="displayFormat"
            name="displayFormat"
            className="w-80"
            items={displayFormats}
            showClearButton={false}
            minSearchLength={0}
            rules={validateRules.displayFormat}
            displayExpr="key"
            valueExpr="value"
          />
        </div>

        {/* 表示範囲 */}
        <div className="flex items-center justify-between">
          <span className="crew-text-gray-4">
            {t('label.displayRange')}
            <CrewRequireDot />
          </span>
          <CrewSelectBoxField
            control={control}
            id="displayRange"
            name="displayRange"
            className="w-80"
            items={displayRanges}
            showClearButton={false}
            minSearchLength={0}
            rules={validateRules.displayRange}
            displayExpr="key"
            valueExpr="value"
          />
        </div>
      </div>

      {/* タスク */}
      <div
        id="personal-setting-display-form-task"
        className="flex flex-col gap-y-2.5 py-2.5 border-b crew-border-gray"
      >
        <p className="font-bold leading-5">{t('label.task')}</p>

        {/* 履歴の表示順 */}
        <div className="flex items-center justify-between">
          <span className="crew-text-gray-4">
            {t('label.historyDisplayOrder')}
            <CrewRequireDot />
          </span>
          <CrewSelectBoxField
            control={control}
            id="historyDisplayOrder"
            name="historyDisplayOrder"
            className="w-80"
            items={historyDisplayOrders}
            showClearButton={false}
            minSearchLength={0}
            rules={validateRules.historyDisplayOrder}
            displayExpr="key"
            valueExpr="value"
          />
        </div>
      </div>

      {/* スケジュール */}
      <div
        id="personal-setting-display-form-schedule"
        className="flex flex-col gap-y-2.5 py-2.5 border-b crew-border-gray"
      >
        <p className="font-bold leading-5">{t('label.schedule')}</p>

        {/* 履歴の表示順 */}
        <div className="flex items-center justify-between">
          <span className="crew-text-gray-4">
            {t('label.initialDisplay')}
            <CrewRequireDot />
          </span>
          <CrewSelectBoxField
            control={control}
            id="scheduleInitialDisplay"
            name="scheduleInitialDisplay"
            className="w-80"
            items={scheduleInitialDisplayItems}
            showClearButton={false}
            minSearchLength={0}
            rules={validateRules.scheduleInitialDisplay}
            displayExpr="key"
            valueExpr="value"
          />
        </div>
      </div>

      {/* Action */}
      <CrewButton
        id="save-personal-settings-button"
        text={t('action.toSave')}
        type="primary"
        className="mr-auto"
        onClick={handleSaveButtonClick}
        disabled={isLoadingInsertOrReplaceSetting}
      />
    </form>
  )
})
